import { throttle } from "ll-common-utilities";
import { animate, inView } from "vendor";
/* ─── gesture-area ──────────────────────────────────────────────────── */
var GestureArea = class {
  #domElement;
  #firstClientX;
  #signal;
  #start;
  #thresholdDistance;
  #thresholdTime;
  #tracking;
  constructor(domElement, { thresholdDistance = 80, thresholdTime = 500, signal = null } = {}) {
    this.#tracking = false;
    this.#start = {};
    this.#domElement = domElement;
    this.#thresholdDistance = thresholdDistance;
    this.#thresholdTime = thresholdTime;
    this.#signal = signal ?? new AbortController().signal;
    this.#domElement.addEventListener("touchstart", this.#touchStart.bind(this), { passive: true, signal: this.#signal });
    this.#domElement.addEventListener("touchmove", this.#preventTouch.bind(this), { passive: false, signal: this.#signal });
    this.#domElement.addEventListener("pointerdown", this.#gestureStart.bind(this), { signal: this.#signal });
    this.#domElement.addEventListener("pointermove", this.#gestureMove.bind(this), { passive: false, signal: this.#signal });
    this.#domElement.addEventListener("pointerup", this.#gestureEnd.bind(this), { signal: this.#signal });
    this.#domElement.addEventListener("pointerleave", this.#gestureEnd.bind(this), { signal: this.#signal });
    this.#domElement.addEventListener("pointercancel", this.#gestureEnd.bind(this), { signal: this.#signal });
  }
  #touchStart(event) {
    this.#firstClientX = event.touches[0].clientX;
  }
  #preventTouch(event) {
    if (Math.abs(event.touches[0].clientX - this.#firstClientX) > 10) {
      event.preventDefault();
    }
  }
  #gestureStart(event) {
    this.#tracking = true;
    this.#start = {
      time: (new Date()).getTime(),
      x: event.clientX,
      y: event.clientY
    };
  }
  #gestureMove(event) {
    if (this.#tracking) {
      event.preventDefault();
    }
  }
  #gestureEnd(event) {
    if (!this.#tracking) {
      return;
    }
    this.#tracking = false;
    const now = (new Date()).getTime(), deltaTime = now - this.#start.time, deltaX = event.clientX - this.#start.x, deltaY = event.clientY - this.#start.y;
    if (deltaTime > this.#thresholdTime) {
      return;
    }
    let matchedEvent;
    if (deltaX === 0 && deltaY === 0) {
      matchedEvent = "tap";
    } else if (deltaX > this.#thresholdDistance && Math.abs(deltaY) < this.#thresholdDistance) {
      matchedEvent = "swiperight";
    } else if (-deltaX > this.#thresholdDistance && Math.abs(deltaY) < this.#thresholdDistance) {
      matchedEvent = "swipeleft";
    } else if (deltaY > this.#thresholdDistance && Math.abs(deltaX) < this.#thresholdDistance) {
      matchedEvent = "swipedown";
    } else if (-deltaY > this.#thresholdDistance && Math.abs(deltaX) < this.#thresholdDistance) {
      matchedEvent = "swipeup";
    }
    if (matchedEvent) {
      this.#domElement.dispatchEvent(new CustomEvent(matchedEvent, { bubbles: true, composed: true, detail: { originalEvent: event } }));
    }
  }
};
/* ─── height-observer ───────────────────────────────────────────────── */
var HeightObserver = class extends HTMLElement {
  #resizeObserver;
  constructor() {
    super(...arguments);
    this.#resizeObserver = new ResizeObserver(throttle(this.#updateCustomProperties.bind(this)));
  }
  connectedCallback() {
    this.#resizeObserver.observe(this);
    if (!window.ResizeObserver) {
      document.documentElement.style.setProperty(`--${this.getAttribute("variable")}-height`, `${this.clientHeight.toFixed(2)}px`);
    }
  }
  disconnectedCallback() {
    this.#resizeObserver.unobserve(this);
  }
  #updateCustomProperties(entries) {
    entries.forEach((entry) => {
      if (entry.target === this) {
        const height = entry.borderBoxSize ? entry.borderBoxSize.length > 0 ? entry.borderBoxSize[0].blockSize : entry.borderBoxSize.blockSize : entry.target.clientHeight;
        document.documentElement.style.setProperty(`--${this.getAttribute("variable")}-height`, `${height.toFixed(2)}px`);
      }
    });
  }
};
if (!window.customElements.get("height-observer")) {
  window.customElements.define("height-observer", HeightObserver);
}
/* ─── loading-bar ───────────────────────────────────────────────────── */
var LoadingBar = class extends HTMLElement {
  constructor() {
    super();
    document.addEventListener("theme:loading:start", this.#onLoadingStart.bind(this));
    document.addEventListener("theme:loading:end", this.#onLoadingEnd.bind(this));
  }
  #onLoadingStart() {
    animate(this, { opacity: [0, 1], transform: ["scaleX(0)", "scaleX(0.4)"] }, { duration: 0.25 });
  }
  async #onLoadingEnd() {
    await animate(this, { transform: [null, "scaleX(1)"] }, { duration: 0.25 }).finished;
    animate(this, { opacity: 0 }, { duration: 0.25 });
  }
};
if (!window.customElements.get("loading-bar")) {
  window.customElements.define("loading-bar", LoadingBar);
}
export {
  GestureArea
};
